import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Typography,
} from "@mui/material";
import { buildPermission } from "portal/utils/auth";
import { capitalize, uncapitalize } from "portal/utils/strings";
import { CarbonCard } from "portal/components/CarbonCard";
import { CopyToClipboardButton } from "portal/components/CopyToClipboardButton";
import { Field } from "formik";
import { findWhere, without } from "portal/utils/arrays";
import { TextField as FormikTextField } from "formik-mui";
import { isUndefined } from "portal/utils/identity";
import { Key } from "@mui/icons-material";
import { ListManager } from "portal/components/ListManager";
import { object, string } from "yup";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { RobotAllowedAlarm, RobotBlockedAlarm } from "protos/portal/admin";
import { skipToken } from "@reduxjs/toolkit/query";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import Grid from "@mui/material/Grid";
import React, { FunctionComponent, useState } from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";

const _RobotAdmin: FunctionComponent = () => {
  const { serial } = useParams();
  const { i18n, t } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  const [setRobotAlarmLists, { isLoading: isSetAlarmListsLoading }] =
    useMutationPopups(portalApi.endpoints.setRobotAlarmLists.useMutation(), {
      success: capitalize(
        t("utils.actions.savedLong", {
          subject: t("views.admin.alarms.lists"),
        })
      ),
    });

  const {
    data: robot,
    isError: isGetRobotError,
    isLoading: isGetRobotLoading,
    isSuccess: isGetRobotSuccess,
    isFetching: isGetRobotFetching,
  } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken
    )
  );

  const [updateRobot] = useMutationPopups(
    portalApi.endpoints.updateRobot.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: uncapitalize(t("models.robots.fields.supportSlack")),
        })
      ),
    }
  );

  const { data: lists } = useQueryPopups(
    portalApi.endpoints.getRobotAlarmLists.useQuery(
      serial ? { serial } : skipToken
    )
  );

  const [getOtp, { isFetching: isOtpFetching }] =
    portalApi.endpoints.getRobotOtp.useLazyQuery();
  const [otpCode, setOtpCode] = useState<string | undefined>();

  const handleGetOtp = async (): Promise<void> => {
    if (!serial) {
      return;
    }
    const { data } = await getOtp({ serial });
    if (data?.code) {
      setOtpCode(data.code);
    }
  };

  if (!serial || !lists) {
    return;
  }

  return (
    <Grid container className="w-full" spacing={2}>
      <CarbonCard
        title={t("models.robots.fields.supportSlack")}
        error={isGetRobotError}
        success={isGetRobotSuccess}
        gridProps={{ size: { xs: 12, md: 6 } }}
        formikProps={{
          validationSchema: object({
            supportSlack: string().required(),
          }),
          initialValues: {
            supportSlack: robot?.robot?.supportSlack.replaceAll("#", "") ?? "",
          },
          onSubmit: async ({ supportSlack }): Promise<void> => {
            if (!supportSlack) {
              return;
            }
            await updateRobot({
              serial,
              robot: { supportSlack: `#${supportSlack.replace(/^#+/, "")}` },
            });
          },
        }}
        content={
          <Field
            disabled={isGetRobotLoading}
            component={FormikTextField}
            name="supportSlack"
            label={t("models.robots.fields.supportSlack")}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">#</InputAdornment>
                ),
              },
            }}
          />
        }
        actions={({ isSubmitting, dirty }) => (
          <Button
            color="secondary"
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={!dirty || isGetRobotFetching}
            startIcon={<SaveIcon />}
          >
            {t("utils.actions.save")}
          </Button>
        )}
      />
      <CarbonCard
        title={t("views.admin.otp.title")}
        error={false}
        success={true}
        gridProps={{ size: { xs: 12, md: 6 } }}
        content={
          <>
            <Typography variant="body2" color="textSecondary">
              {t("views.admin.otp.description")}
            </Typography>
            <Alert severity="warning" className="mt-2">
              {t("views.admin.otp.warning")}
            </Alert>
            {otpCode && (
              <div className="mt-3 flex items-center gap-2">
                <Typography
                  variant="h5"
                  component="span"
                  style={{ fontFamily: "monospace" }}
                >
                  {otpCode}
                </Typography>
                <CopyToClipboardButton text={otpCode} />
              </div>
            )}
          </>
        }
        actions={
          <Button
            color="secondary"
            variant="contained"
            loading={isOtpFetching}
            onClick={handleGetOtp}
            startIcon={<Key />}
          >
            {t("views.admin.otp.getCode")}
          </Button>
        }
      />
      <CarbonCard
        title={t("views.admin.alarms.titleAllow")}
        error={false}
        success={true}
        content={
          <>
            <Alert severity="warning" className="mt-1">
              {t("views.admin.alarms.allowWarning")}
            </Alert>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lists.forwardAll}
                    onChange={(e) => {
                      setRobotAlarmLists({
                        serial,
                        lists: { ...lists, forwardAll: e.target.checked },
                      });
                    }}
                    disabled={isSetAlarmListsLoading}
                  />
                }
                label={t("views.admin.alarms.forwardAll")}
              />
              <Typography
                variant="caption"
                className="block"
                color="textSecondary"
              >
                {t("views.admin.alarms.forwardAllHint")}
              </Typography>
            </div>
            {!lists.forwardAll && (
              <>
                <ListManager
                  list={lists.allowed?.alarms.map((alarm) => alarm.code)}
                  loading={isSetAlarmListsLoading}
                  onAdd={(newCode) => {
                    setRobotAlarmLists({
                      serial,
                      lists: {
                        ...lists,
                        allowed: {
                          alarms: [
                            ...(lists.allowed?.alarms ?? []),
                            RobotAllowedAlarm.fromPartial({ code: newCode }),
                          ],
                        },
                      },
                    });
                  }}
                  onRemove={(removedCode) => {
                    setRobotAlarmLists({
                      serial,
                      lists: {
                        ...lists,
                        allowed: {
                          alarms: without(
                            lists.allowed?.alarms,
                            findWhere(lists.allowed?.alarms, {
                              code: removedCode,
                            })
                          ).filter((alarm) => !isUndefined(alarm)),
                        },
                      },
                    });
                  }}
                />
              </>
            )}
          </>
        }
      />
      <CarbonCard
        title={t("views.admin.alarms.titleBlock")}
        error={false}
        success={true}
        content={
          <>
            <Alert severity="warning">
              {t("views.admin.alarms.blockWarning")}
            </Alert>
            <ListManager
              list={lists.blocked?.alarms.map((alarm) => alarm.code)}
              loading={isSetAlarmListsLoading}
              onAdd={(newCode) => {
                setRobotAlarmLists({
                  serial,
                  lists: {
                    ...lists,
                    blocked: {
                      alarms: [
                        ...(lists.blocked?.alarms ?? []),
                        RobotBlockedAlarm.fromPartial({ code: newCode }),
                      ],
                    },
                  },
                });
              }}
              onRemove={(removedCode) => {
                setRobotAlarmLists({
                  serial,
                  lists: {
                    ...lists,
                    blocked: {
                      alarms: without(
                        lists.blocked?.alarms,
                        findWhere(lists.blocked?.alarms, {
                          code: removedCode,
                        })
                      ).filter((alarm) => !isUndefined(alarm)),
                    },
                  },
                });
              }}
            />
          </>
        }
      />
    </Grid>
  );
};

export const RobotAdmin = withAuthorizationRequired(
  buildPermission(
    PermissionAction.read,
    PermissionResource.admin_cloud,
    PermissionDomain.all
  ),
  _RobotAdmin
);
